export type MappedFromRow = {
  manufacturer: string;
  egmCount: number;
  activeCount: number;
};

export type ManagedRow = {
  slotcheckManufacturer: string;
  egmCount: number;
  activeCount: number;
  manufacturers: string[];
  mappedFrom?: MappedFromRow[];
};

export type UnmappedRow = {
  manufacturer: string;
  egmCount: number;
  activeCount: number;
};

export type SortKey = 'name' | 'egmCount' | 'activeCount';

/** Normalize mappedFrom from API (or derive from manufacturers[]). */
export function mappedFromForRow(row: ManagedRow): MappedFromRow[] {
  if (Array.isArray(row.mappedFrom) && row.mappedFrom.length > 0) {
    return row.mappedFrom
      .map((m) => ({
        manufacturer: String(m.manufacturer || '').trim(),
        egmCount: Number(m.egmCount) || 0,
        activeCount: Number(m.activeCount) || 0,
      }))
      .filter((m) => m.manufacturer)
      .sort((a, b) => a.manufacturer.localeCompare(b.manufacturer));
  }
  return (row.manufacturers || [])
    .map((m) => String(m || '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .map((manufacturer) => ({ manufacturer, egmCount: 0, activeCount: 0 }));
}

export function filterSortManaged(
  rows: ManagedRow[],
  q: string,
  sortKey: SortKey,
  sortDir: 'asc' | 'desc'
): ManagedRow[] {
  const needle = q.trim().toLowerCase();
  let list = rows;
  if (needle) {
    list = rows.filter((r) => {
      if (
        String(r.slotcheckManufacturer || '')
          .toLowerCase()
          .includes(needle)
      ) {
        return true;
      }
      const mapped = mappedFromForRow(r);
      if (mapped.some((m) => m.manufacturer.toLowerCase().includes(needle))) {
        return true;
      }
      return (r.manufacturers || []).some((m) =>
        String(m || '')
          .toLowerCase()
          .includes(needle)
      );
    });
  }
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    if (sortKey === 'name') {
      return dir * a.slotcheckManufacturer.localeCompare(b.slotcheckManufacturer);
    }
    return dir * ((a[sortKey] || 0) - (b[sortKey] || 0));
  });
}

export function filterSortUnmapped(
  rows: UnmappedRow[],
  q: string,
  sortKey: SortKey,
  sortDir: 'asc' | 'desc'
): UnmappedRow[] {
  const needle = q.trim().toLowerCase();
  let list = rows;
  if (needle) {
    list = rows.filter((r) =>
      String(r.manufacturer || '')
        .toLowerCase()
        .includes(needle)
    );
  }
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    if (sortKey === 'name') {
      return dir * a.manufacturer.localeCompare(b.manufacturer);
    }
    return dir * ((a[sortKey] || 0) - (b[sortKey] || 0));
  });
}

/** Confirm dialog copy for the set-slotcheckManufacturer action. */
export function setManufacturerConfirmMessage(
  manufacturer: string,
  slotcheckManufacturer: string,
  egmCount: number
): string {
  return `Update ${egmCount.toLocaleString()} EGM${
    egmCount === 1 ? '' : 's'
  } with manufacturer "${manufacturer}" to slotcheckManufacturer "${slotcheckManufacturer}"? The raw manufacturer field is never changed.`;
}

/** Confirm dialog copy for Unmap. */
export function unmapManufacturerConfirmMessage(
  manufacturer: string,
  slotcheckManufacturer: string,
  egmCount: number
): string {
  return `Unmap ${egmCount.toLocaleString()} EGM${
    egmCount === 1 ? '' : 's'
  } with manufacturer "${manufacturer}" from slotcheckManufacturer "${slotcheckManufacturer}"? They will appear in Unmapped. The raw manufacturer field is never changed.`;
}

/** Toggle a SlotCheck group key in the expanded-set (multi-open accordion). */
export function toggleExpandedGroup(
  expanded: ReadonlySet<string> | string[],
  slotcheckManufacturer: string
): Set<string> {
  const next = new Set(expanded);
  if (next.has(slotcheckManufacturer)) next.delete(slotcheckManufacturer);
  else next.add(slotcheckManufacturer);
  return next;
}
